/**
 * The shape every api atom has: a snapshot to read, and write atoms that load it again.
 * `snapshotAtoms` owns the mechanism so each feature writes only its loader.
 *
 * `load` resolves to the next snapshot and may reject; a rejection becomes the snapshot's
 * `error`. So a read is always synchronous and always a value, and no call site needs Suspense
 * or an error boundary.
 *
 * A load runs from a write atom, never from a read: a read can only return a value, while what
 * follows a load — `onLoaded` — writes other atoms. The same write holds the load's
 * `AbortController`, so a newer load aborts the one in flight and so does the last reader
 * leaving. An aborted load writes nothing.
 *
 * Nothing is requested until something subscribes, and a subscriber returning after all have
 * left reloads — a panel closed and reopened shows the data as it is now, not as it was when
 * first opened. Both come from one `onMount`, which runs once per unmounted → mounted
 * transition.
 *
 * A reload asked for while nothing reads the snapshot requests nothing: the next reader reloads
 * on mount anyway, so a pushed change to a closed panel costs no request, and its reopening
 * costs one. Until that mount the snapshot reads as loading, so the reader's first render does
 * not take the data the change replaced for current.
 *
 * While something reads the snapshot, `reload` and `reloadNow` always start a load, because a
 * change frame must never be absorbed by a load that started before the change. `reload` waits
 * `reloadAfterMs` when the resource sets it, so a burst of change frames costs one request;
 * `reloadNow` is for a retry the operator asks for. `revalidate` is for a reader that only wants
 * the data fresh as of its opening: a load in flight, the first one included, or one settled
 * within `FRESH_FOR_MS`, answers it.
 *
 * A write that answers with the resource's new state hands it to `answer`. The answer is newer
 * than any load in flight, so it aborts that load and cancels a delayed reload. A reload asked
 * for after it loads again as usual.
 *
 * A snapshot built from pages comes from `pagedSnapshotAtoms`. Each page that settles goes
 * through `merge` into the snapshot held at that moment. The older page loads from the held rows,
 * so it needs a reader and a load that succeeded. A caller sends it only while `olderState` is
 * not `Loading`: while the page is in flight nothing changes the held rows, so a second one would
 * ask for the same page and abort the first. It and a refresh abort each other, and every
 * reload aborts it. The poll tick skips while any load is in flight, because a poll that
 * aborted the load on each interval would starve a slow load. An older page that aborted a
 * refresh, from a tick, a command or a change, starts that refresh again when it settles,
 * whether it succeeded or failed, so the change is not lost. A refresh that aborts the older
 * page, or the last reader leaving, ends that restart. An older page leaves a waiting delayed
 * reload alone.
 *
 * `apply` takes a change that the resource names, as a function from the held rows to a `Patched`
 * snapshot, to `Reread` or to `Reset`. A merge keeps the held rows that a page does not have, so a
 * change that makes them untrue answers `Reset`: the rows go, and the load runs as the first load
 * does, from nothing. Before a load succeeded, as during the first load, it rereads and never
 * calls that function. A reread is a `reloadNow`. A patch is newer than any load in flight, so it
 * aborts that load and holds the snapshot with no request, with the error it gives back. It starts
 * a refresh only if it aborted one, or an older page that was keeping one, so the change that
 * refresh meant to load is not lost. A patch aborting an older page starts nothing, and a delayed
 * reload that waits stays. While nothing reads, a patch holds the rows and marks the snapshot
 * stale, so the next mount loads once. A patch that gives back the held snapshot is a change that
 * the rows do not take, so it aborts nothing and changes nothing.
 *
 * A reload keeps the previous snapshot on screen under `loading: true`, so a consumer that renders
 * a spinner still renders one and never sees the data go blank and come back. A failure keeps that
 * data too, by one of two rules. A refresh that fails sets `error` on it: a list stays on screen
 * through a network glitch rather than emptying everywhere it is read. Only a first load that
 * fails reads as `pendingSnapshot`. An older page that fails keeps the snapshot as it was, the
 * same object, and sets `hasOlderFailed`, which `olderState` reads as `Failed`: the held rows are
 * still current, and only the older rows are missing. Only the next older page clears it. A
 * refresh and an unmount keep it, so a reader that mounts again still sees that the page failed.
 */

import { atom, type WritableAtom } from 'jotai';
import { atomWithReset, RESET } from 'jotai/utils';

import { toError } from '@/lib/failureMessage';
import { SECOND_MS } from '@/lib/timeUnits';
import {
    ApplyOutcome, type ApplyResult, type LoadState, OlderState, type Options, type PagedOptions,
    type PagedSnapshotAtoms, type SnapshotAtoms, SnapshotCommand, type SnapshotWrite,
} from '@/types/snapshot';

/** A reader mounting right behind another's load, like a panel behind the gate, reuses it. */
export const FRESH_FOR_MS = 2 * SECOND_MS;

enum LoadKind {
    Refresh,
    Older,
}

interface Flight {
    controller: AbortController;
    kind: LoadKind;
    /** An older page that aborted a refresh starts it again when it settles. */
    restartsRefresh: boolean;
}

interface State<S> {
    /** What the last settled load left: its snapshot, or the one before it. */
    lastSnapshot: S | null;
    /** True once a load succeeded or an answer came; a failed load leaves it as it was. */
    isLoaded: boolean;
    /** The load in flight. */
    flight: Flight | null;
    answeredAtMs: number | null;
    /** The last older page failed. Only the next older page clears it. */
    hasOlderFailed: boolean;
}

enum Lifecycle {
    Mount,
    Tick,
    Unmount,
}

export function snapshotAtoms<S extends LoadState>(
    pendingSnapshot: S,
    load: (signal: AbortSignal) => Promise<S>,
    options: Options<S> = {},
): SnapshotAtoms<S> {
    return pagedSnapshotAtoms(pendingSnapshot, (signal) => load(signal),
        (_previousSnapshot, page) => page, options);
}

/**
 * The core of `snapshotAtoms`, for a snapshot built from pages. `merge` puts each page that
 * settles into the snapshot held at that time. The file comment gives the rules of the loads.
 */
export function pagedSnapshotAtoms<S extends LoadState, P>(
    pendingSnapshot: S,
    load: (signal: AbortSignal, previousSnapshot: S | null) => Promise<P>,
    merge: (previousSnapshot: S | null, page: P) => S,
    {
        refreshEveryMs, reloadAfterMs, onLoaded, loadOlder, isStaleWhenUnread = false,
    }: PagedOptions<S, P> = {},
): PagedSnapshotAtoms<S> {
    const stateAtom = atomWithReset<State<S>>({
        lastSnapshot: null, isLoaded: false, flight: null, answeredAtMs: null,
        hasOlderFailed: false,
    });
    const mountedAtom = atom(false);
    // Held data the next load must replace before it reads as settled: a reload asked for while
    // nothing read the snapshot, or `isStaleWhenUnread`.
    const staleAtom = atom(false);
    const reloadTimerAtom = atom<ReturnType<typeof setTimeout> | null>(null);

    const cancelReloadAtom = atom(null, (get, set): void => {
        const timer = get(reloadTimerAtom);
        if (timer !== null) {
            clearTimeout(timer);
            set(reloadTimerAtom, null);
        }
    });

    // Inside its own write, `set(loadAtom)` sets the atom's value instead of running its write.
    const restartAtom: WritableAtom<null, [], void> = atom(null, (_get, set): void => {
        set(loadAtom, LoadKind.Refresh);
    });

    const loadAtom = atom(null, (get, set, kind: LoadKind): void => {
        const { lastSnapshot: previousSnapshot, isLoaded } = get(stateAtom);
        // The cursor of an older page comes from the rows of a load that succeeded.
        if (kind === LoadKind.Older &&
            (loadOlder === undefined || !get(mountedAtom) || !isLoaded)) {
            return;
        }
        // An older page leaves a waiting reload alone: that reload is a change to load.
        if (kind === LoadKind.Refresh) {
            set(cancelReloadAtom);
            set(staleAtom, false);
        }
        const previous = get(stateAtom).flight;
        previous?.controller.abort();
        const controller = new AbortController();
        const flight: Flight = {
            controller,
            kind,
            restartsRefresh: kind === LoadKind.Older && previous !== null &&
                (previous.kind === LoadKind.Refresh || previous.restartsRefresh),
        };
        const startedAtMs = Date.now();
        // An older page that starts is the retry of the one that failed.
        const hasOlderFailed = kind !== LoadKind.Older && get(stateAtom).hasOlderFailed;
        set(stateAtom, { ...get(stateAtom), flight, hasOlderFailed });
        // The executor runs now, and turns a loader that throws into a rejection.
        new Promise<P>((resolve) => {
            resolve(kind === LoadKind.Older && loadOlder !== undefined && previousSnapshot !== null
                ? loadOlder(controller.signal, previousSnapshot)
                : load(controller.signal, previousSnapshot));
        }).then(
            (page) => {
                if (controller.signal.aborted) {
                    return;
                }
                const snapshot: S = {
                    ...merge(get(stateAtom).lastSnapshot, page),
                    isLoading: false,
                    error: null,
                };
                set(stateAtom, {
                    ...get(stateAtom),
                    lastSnapshot: snapshot,
                    isLoaded: true,
                    flight: null,
                    answeredAtMs: Date.now(),
                });
                onLoaded?.(snapshot, startedAtMs, get, set);
                if (flight.restartsRefresh) {
                    set(restartAtom);
                }
            },
            (error: unknown) => {
                if (controller.signal.aborted) {
                    return;
                }
                const state = get(stateAtom);
                if (flight.kind === LoadKind.Older) {
                    set(stateAtom, { ...state, flight: null, hasOlderFailed: true });
                } else {
                    set(stateAtom, {
                        ...state,
                        lastSnapshot: { ...state.lastSnapshot ?? pendingSnapshot, isLoading: false,
                            error: toError(error) },
                        flight: null,
                        answeredAtMs: Date.now(),
                    });
                }
                if (flight.restartsRefresh) {
                    set(restartAtom);
                }
            },
        );
    });

    const tickAtom = atom(null, (get, set): void => {
        if (get(stateAtom).flight === null) {
            set(reloadNowAtom);
        }
    });

    // The snapshot reads this, so it mounts with the snapshot: its onMount is the one place
    // that learns when something starts or stops reading.
    const lifecycleAtom = atom(
        (get): State<S> => get(stateAtom),
        (get, set, event: Lifecycle): void => {
            set(mountedAtom, event !== Lifecycle.Unmount);
            if (event === Lifecycle.Tick) {
                set(tickAtom);
                return;
            }
            if (event !== Lifecycle.Unmount) {
                set(loadAtom, LoadKind.Refresh);
                return;
            }
            const state = get(stateAtom);
            // A reload nobody answered leaves the data it meant to replace.
            if (isStaleWhenUnread || state.flight !== null || get(reloadTimerAtom) !== null) {
                set(staleAtom, true);
            }
            set(cancelReloadAtom);
            state.flight?.controller.abort();
            set(stateAtom, { ...state, flight: null });
        },
    );
    lifecycleAtom.onMount = (dispatch): (() => void) => {
        dispatch(Lifecycle.Mount);
        const timer = refreshEveryMs === undefined
            ? undefined
            : setInterval(() => dispatch(Lifecycle.Tick), refreshEveryMs);
        return (): void => {
            clearInterval(timer);
            dispatch(Lifecycle.Unmount);
        };
    };

    const olderStateAtom = atom((get): OlderState => {
        const { flight, hasOlderFailed } = get(stateAtom);
        if (flight?.kind === LoadKind.Older) {
            return OlderState.Loading;
        }
        return hasOlderFailed ? OlderState.Failed : OlderState.Idle;
    });

    const valueAtom = atom((get): S => {
        const { lastSnapshot, flight } = get(lifecycleAtom);
        if (flight === null && !get(staleAtom)) {
            return lastSnapshot ?? pendingSnapshot;
        }
        return lastSnapshot ? { ...lastSnapshot, isLoading: true } : pendingSnapshot;
    });

    const reloadNowAtom = atom(null, (get, set): void => {
        if (get(mountedAtom)) {
            set(loadAtom, LoadKind.Refresh);
        } else {
            set(staleAtom, true);
        }
    });

    const answerAtom = atom(null, (get, set, snapshot: S): void => {
        // Nothing reads it: the next reader loads on mount, as after a reload.
        if (!get(mountedAtom)) {
            return;
        }
        set(cancelReloadAtom);
        set(staleAtom, false);
        get(stateAtom).flight?.controller.abort();
        set(stateAtom, {
            ...get(stateAtom),
            lastSnapshot: { ...snapshot, isLoading: false, error: null },
            isLoaded: true,
            flight: null,
            answeredAtMs: Date.now(),
        });
    });

    const applyAtom = atom(null, (
        get, set, resolve: (previousSnapshot: S) => ApplyResult<S>,
    ): void => {
        const { lastSnapshot, isLoaded, flight } = get(stateAtom);
        const result = lastSnapshot === null || !isLoaded ? null : resolve(lastSnapshot);
        if (result === null || result.outcome === ApplyOutcome.Reread) {
            set(reloadNowAtom);
            return;
        }
        if (result.outcome === ApplyOutcome.Reset) {
            flight?.controller.abort();
            set(stateAtom, RESET);
            set(reloadNowAtom);
            return;
        }
        if (result.snapshot === lastSnapshot) {
            return;
        }
        const snapshot: S = { ...result.snapshot, isLoading: false };
        if (!get(mountedAtom)) {
            set(stateAtom, { ...get(stateAtom), lastSnapshot: snapshot });
            set(staleAtom, true);
            return;
        }
        const restartsRefresh = flight !== null &&
            (flight.kind === LoadKind.Refresh || flight.restartsRefresh);
        flight?.controller.abort();
        set(stateAtom, {
            ...get(stateAtom),
            lastSnapshot: snapshot,
            isLoaded: true,
            flight: null,
            answeredAtMs: Date.now(),
        });
        if (restartsRefresh) {
            set(loadAtom, LoadKind.Refresh);
        }
    });

    return {
        value: valueAtom,
        reload: atom(null, (get, set): void => {
            if (reloadAfterMs === undefined) {
                set(reloadNowAtom);
                return;
            }
            if (!get(mountedAtom)) {
                set(staleAtom, true);
                return;
            }
            if (get(reloadTimerAtom) !== null) {
                return;
            }
            set(reloadTimerAtom, setTimeout(() => {
                set(reloadTimerAtom, null);
                set(loadAtom, LoadKind.Refresh);
            }, reloadAfterMs));
        }),
        reloadNow: reloadNowAtom,
        revalidate: atom(null, (get, set): void => {
            const { flight, answeredAtMs } = get(stateAtom);
            const isFresh = flight !== null ||
                (answeredAtMs !== null && Date.now() - answeredAtMs < FRESH_FOR_MS);
            if (!isFresh) {
                set(reloadNowAtom);
            }
        }),
        answer: answerAtom,
        olderReload: atom(null, (_get, set): void => {
            set(loadAtom, LoadKind.Older);
        }),
        olderState: olderStateAtom,
        tick: tickAtom,
        apply: applyAtom,
    };
}

/** A snapshot as one atom, for an atom family: read it for the snapshot, set it to command. */
export function snapshotMemberAtom<S extends LoadState>(
    snapshot: SnapshotAtoms<S>,
): WritableAtom<S, [SnapshotWrite<S>], void> {
    return atom(
        (get): S => get(snapshot.value),
        (_get, set, write: SnapshotWrite<S>): void => {
            switch (write.command) {
                case SnapshotCommand.Reload:
                    set(snapshot.reload);
                    break;
                case SnapshotCommand.ReloadNow:
                    set(snapshot.reloadNow);
                    break;
                case SnapshotCommand.Answer:
                    set(snapshot.answer, write.snapshot);
                    break;
                default:
                    break;
            }
        },
    );
}
